import {
  PropertyCountry as PropertyCountryIcon,
  LandlordCountry as LandlordCountryIcon,
} from 'common/components/svgs';
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { CloseOutlined, CopyOutlined, DeleteOutlined } from '@ant-design/icons';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Popconfirm, Tooltip, Button } from 'antd';
import trim from 'lodash/trim';
import find from 'lodash/find';
import classNames from 'classnames';

import modal from '~components/modal';
import DetailsForm from '~components/commission/commission-form-modal/details-form';
import ExtraRequirementForm from '~components/commission/commission-form-modal/extra-requirement-form';
import CommissionCapForm from '~components/commission/commission-form-modal/commission-cap-form';
import NoteForm from '~components/commission/commission-form-modal/note-form';
import PropertiesForm from '~components/commission/commission-form-modal/properties-form';
import {
  allCommissionCategories,
  numBookings,
  tenancyLength,
  flatFee,
  defaultField,
  PreCalCommissionWhenPropertyTierChangeAction,
} from '~constants/commission';
import * as commissionActions from '~actions/properties/commission';
import showElementByAuth from '~helpers/auth';
import { platformEntity, entityAction, commissionLevel as commissionLevelConst } from '~constants';
import { dayFormatType, formatDay, moment } from '~helpers/day';
import ReviewCommission from '../review-commission/modal';
import { PopconfirmWrapper as DeletePopconfirm } from '../commission-list/index';
import TriggerPointForm from './trigger-point-form';
import CreateForm from './create-form';

const mapDispatchToProps = dispatch => ({
  createCommission: (data, callback, onError) => {
    dispatch(commissionActions.createCommission(data, callback, onError));
  },
  updateCommission: (data, callback) => {
    dispatch(commissionActions.updateCommission(data, callback));
  },
  deleteCommission: (data, callback) => {
    dispatch(commissionActions.deleteCommission(data, callback));
  },
  bulkCreateCommissionTiers: (commissionTiers, callback) => {
    dispatch(commissionActions.bulkCreateCommissionTiers(commissionTiers, callback));
  },
});

@connect(null, mapDispatchToProps)
@modal({ className: 'commission-from-modal' }, true)
class CommissionFormModal extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      commissionDetails: null,
      isFieldsChange: false,
      showPreviewPopconfirm: false,
      previewCommissionTier: null,
      previewCommissionProperties: null,
      previewCommissionAction: null,
      triggerPoint: this.props.triggerPoint,
    };

    this.dataTypes = ['effectiveFrom', 'effectiveTo', 'checkInDateFrom', 'checkInDateTo'];
  }

  componentDidMount() {
    this.getCommissionDetails();
    // add browser alert
    window.onbeforeunload = e => {
      if (this.props.form.isFieldsTouched()) {
        const msg = this.props.t('cms.properties.edit.leave_alert.content');
        // eslint-disable-next-line no-param-reassign
        e = e || window.event;
        if (e) {
          e.returnValue = msg;
        }

        return msg;
      }
      return null;
    };
  }

  // eslint-disable-next-line class-methods-use-this
  componentWillUnmount() {
    window.onbeforeunload = null;
  }

  componentDidUpdate(prevProps) {
    if (this.props.commissionCategory !== prevProps.commissionCategory) {
      this.getCommissionDetails(true);
    }
  }

  getCommissionDetails = isChange => {
    if (this.props.correlationId && this.props.commissionTiers) {
      const details = this.props.commissionTiers.find(commissionTier => {
        if (commissionTier.id === this.props.correlationId) {
          return commissionTier;
        }

        return null;
      });

      const newDetails = { ...details };

      if (isChange) {
        newDetails.category = this.props.commissionCategory;
      }

      this.setState({
        commissionDetails: newDetails,
      });

      this.setModalDetails(newDetails);
    }

    if (this.props.modalType === 'addNew') {
      this.setModalDetails(this.props.form.getFieldsValue());
    }
  };

  setModalDetails = details => {
    const commissionDetails = { ...details };
    const formFields = this.getAllFields();
    const data = {};

    formFields.map(field => {
      if (!(field === 'capType' && !commissionDetails[field])) {
        data[field] = commissionDetails[field];
      }

      if (
        ['bonus', 'fullyCalculatable', 'retrospectiveCommission'].indexOf(field) !== -1 &&
        !commissionDetails[field]
      ) {
        data[field] = false;
      }

      return true;
    });

    if (this.props.modalType !== 'addNew' && commissionDetails) {
      data.fullyCalculatable = !commissionDetails.fullyCalculatable;

      this.dataTypes.forEach(type => {
        if (commissionDetails[type]) {
          data[type] = commissionDetails[type] ? moment(commissionDetails[type]) : null;
        }
      });
    }

    if (this.props.modalType === 'addNew') {
      if (this.props.commissionCategory) {
        data.category = this.props.commissionCategory;
      }
    }

    if (this.props.modalType === 'copy') {
      data.name = this.props.t('cms.property.commission_form_modal.copy.title', {
        name: data.name,
      });
    }

    if (Object.keys(data)?.length > 0 && data.currencyValues) {
      let temp = {};
      Object.keys(data.currencyValues).forEach(key => {
        temp[key?.toUpperCase()] = data.currencyValues[key];
      });

      this.props.form.setFieldsValue({ ...data, currencyValues: temp });
    } else {
      this.props.form.setFieldsValue(data);
    }
  };

  getAllFields = () => {
    if (this.state.triggerPoint === 'CREATED') {
      return [
        'category',
        'name',
        'type',
        'value',
        'effectiveFrom',
        'effectiveTo',
        'currencyValues',
        'rank',
        'bookingJourney',
        // 'checkInDateFrom',
        // 'checkInDateTo',
      ];
    }

    const { commissionCategory, modalType } = this.props;
    let allFields = defaultField;

    allFields = allFields.concat('unitTypeIds');

    if (commissionCategory === allCommissionCategories.TENANCY_LENGTH) {
      allFields = allFields.concat(tenancyLength);
    }

    if (commissionCategory === allCommissionCategories.FLAT_FEE) {
      allFields = allFields.concat(flatFee);
    }

    if (commissionCategory === allCommissionCategories.NUM_BOOKINGS) {
      allFields = allFields.concat(numBookings);
    }

    if (!commissionCategory && modalType === 'addNew') {
      allFields = allFields.concat(['fullyCalculatable', 'retrospectiveCommission']);
    }

    if (modalType !== 'edit') {
      allFields = allFields.concat('landlordProperties');
    }

    return allFields;
  };

  handleSubmit = e => {
    e.preventDefault();
    const {
      form,
      modalType,
      correlationId,
      property,
      landlord,
      commissionLevel,
      onClose,
      createCommission,
      updateCommission,
      bulkCreateCommissionTiers,
      getLandlord = () => {},
    } = this.props;
    const formField = this.getAllFields();

    form.validateFieldsAndScroll(
      formField,
      { scroll: { offsetTop: 70, offsetBottom: 70 } },
      (err, values) => {
        if (!err) {
          if (values) {
            const data = values;

            const isApplyRooms = form.getFieldValue('applyRooms') === 'allRooms';

            data.name = trim(data.name);
            if (isApplyRooms) {
              data['unitTypeIds'] = [];
            }

            data.retrospectiveCommission = !!values.retrospectiveCommission;
            data.fullyCalculatable = !values.fullyCalculatable;
            if (values.tenancyUnit === 'NOT_SPECIFIC') {
              data.tenancyUnit = null;
            }

            if (values.capType === 'NOT_SPECIFIC') {
              data.capType = null;
            }

            this.dataTypes.forEach(dataType => {
              if (data[dataType] && values[dataType]) {
                data[dataType] = formatDay(dayFormatType.FILTERPARAMS, values[dataType]);
              }
            });

            if (modalType === 'edit' && correlationId) {
              data.id = correlationId;
              delete data.bookingJourney;
              updateCommission(data, () => {
                getLandlord();
                onClose();
              });
            } else {
              if (commissionLevel === commissionLevelConst.PROPERTY) {
                data.propertyId = property.id;
              } else if (commissionLevel === commissionLevelConst.LANDLORD) {
                data.landlordId = landlord.id;

                delete data.unitTypeIds;
                delete data.listingId;
              }

              if (form.getFieldValue('applyType') === 'moreProperties') {
                const commissionTiers = [];
                const targetPropertyIds = form.getFieldValue('landlordProperties');

                if (targetPropertyIds) {
                  targetPropertyIds.map(propertyId => {
                    commissionTiers.push(Object.assign({}, data, { propertyId }));
                    return true;
                  });
                } else {
                  commissionTiers.push(Object.assign({}, data));
                }
                bulkCreateCommissionTiers(commissionTiers, getLandlord);
              } else {
                if (this.state.triggerPoint === 'CREATED') {
                  data.triggerPoint = this.state.triggerPoint;
                  data.type = 'VALUE';
                }

                createCommission(data, () => {
                  getLandlord();
                  onClose();
                });
              }
            }
          }
        }
      },
    );
  };

  closePopModal = () => {
    this.setState({
      isFieldsChange: false,
    });
  };

  closeCommissionModal = () => {
    this.setState({
      isFieldsChange: this.props.form.isFieldsTouched(),
    });

    if (!this.props.form.isFieldsTouched()) {
      this.props.onClose();
    }
  };

  handleDuplicate = () => {
    const data = this.props.form.getFieldsValue();
    data.id = this.props.correlationId;

    if (data) {
      if (data.retrospectiveCommission) {
        data.retrospectiveCommission = !!data.retrospectiveCommission;
      }

      this.dataTypes.forEach(dataType => {
        if (data[dataType]) {
          data[dataType] = formatDay(dayFormatType.FILTERPARAMS, data[dataType]);
        }
      });
    }

    if (document.querySelector('.commission-from-modal')) {
      document
        .querySelector('.commission-from-modal')
        .classList.add('commission-from-modal__animation');
    }

    const copyName = this.props.t('cms.property.commission_form_modal.copy.title', {
      name: data.name,
    });
    this.props.form.setFieldsValue({ name: copyName });

    setTimeout(() => {
      this.props.openModal('copy', data);
    }, 1000);
  };

  handleDeleteCommission = () => {
    const { correlationId } = this.props;
    this.props.onClose();
    this.props.deleteCommission({ id: correlationId });
  };

  handleGotoPreview = previewAction => {
    const { form, modalType, correlationId, property, landlord, commissionLevel } = this.props;
    const formField = this.getAllFields();

    form.validateFields(formField, (err, values) => {
      if (!err) {
        if (values) {
          const data = values;
          const isApplyRooms = form.getFieldValue('applyRooms') === 'allRooms';

          data.name = trim(data.name);
          if (isApplyRooms) {
            data['unitTypeIds'] = [];
          }

          data.retrospectiveCommission = !!values.retrospectiveCommission;
          data.fullyCalculatable = !values.fullyCalculatable;
          if (values.tenancyUnit === 'NOT_SPECIFIC') {
            data.tenancyUnit = null;
          }

          if (values.capType === 'NOT_SPECIFIC') {
            data.capType = null;
          }

          this.dataTypes.forEach(dataType => {
            if (data[dataType] && values[dataType]) {
              data[dataType] = formatDay(dayFormatType.FILTERPARAMS, values[dataType]);
            }
          });

          if (modalType === 'edit' && correlationId) {
            data.id = correlationId;
            // updateCommission(data, getLandlord);
            if (
              !form.isFieldTouched('bonus') ||
              data.bonus === this.state.commissionDetails?.bonus
            ) {
              delete data.bonus;
            }
            this.setState({
              previewCommissionTier: data,
              previewCommissionAction: previewAction,
              previewCommissionProperties: [property],
            });
          } else {
            if (commissionLevel === commissionLevelConst.PROPERTY) {
              data.propertyId = property.id;
            } else if (commissionLevel === commissionLevelConst.LANDLORD) {
              data.landlordId = landlord.id;

              delete data.unitTypeIds;
              delete data.listingId;
            }

            if (form.getFieldValue('applyType') === 'moreProperties') {
              const properties = [];
              const targetPropertyIds = form.getFieldValue('landlordProperties');

              if (targetPropertyIds) {
                targetPropertyIds.map(propertyId => {
                  properties.push({ id: propertyId });
                  return true;
                });
              } else {
                properties.push(property);
              }
              delete data.landlordProperties;
              this.setState({
                previewCommissionTier: data,
                previewCommissionAction: previewAction,
                previewCommissionProperties: properties,
              });
            } else {
              this.setState({
                previewCommissionTier: data,
                previewCommissionAction: previewAction,
                previewCommissionProperties: [property],
              });
            }
          }

          // onClose();
        }
      }
    });
  };

  handleTriggerPointChange = value => {
    this.setState({
      triggerPoint: value,
    });
    if (value === 'CREATED') {
      this.props.form.setFieldsValue({
        category: 'FLAT_FEE',
      });
    }
  };

  render() {
    const {
      t,
      property,
      landlord,
      modalType,
      commissionLevel,
      commissionCategory,
      landlordProperties,
      commissionRooms: { edges },
      correlationId,
      commissionTiers,
    } = this.props;

    const { handleSubmit } = this;

    const currentCommissionTier = find(commissionTiers, ['id', correlationId]);

    const isCreateOrder = this.state.triggerPoint === 'CREATED';

    return (
      <div className="commission-from">
        <h2 className="commission-from__title">
          <Choose>
            <When condition={modalType === 'edit'}>
              {t('cms.property.commission.create.create_commission_modal.edit.title')}
            </When>
            <Otherwise>
              {t('cms.property.commission.create.create_commission_modal.add.title')}
            </Otherwise>
          </Choose>
        </h2>

        <Popconfirm
          trigger="click"
          visible={this.state.isFieldsChange}
          title={t('cms.property.commission_form_modal.close.tips.title')}
          placement="left"
          onConfirm={this.props.onClose}
          onCancel={this.closePopModal}
          okText={t('cms.properties.edit.btn.yes')}
          cancelText={t('cms.properties.edit.btn.no')}
        >
          <button onClick={this.closeCommissionModal} className="commission-from__close-btn">
            <CloseOutlined style={{ fontSize: '12px', color: '#9e9e9e' }} />
          </button>
        </Popconfirm>

        <div className="commission-from__content">
          <If condition={modalType === 'edit' && commissionLevel === commissionLevelConst.PROPERTY}>
            <PropertyCountryIcon className="commission-from__country-icon" />
            <span className="commission-from__property-name">{property?.name}</span>
          </If>

          <If condition={commissionLevel === commissionLevelConst.LANDLORD}>
            <LandlordCountryIcon className="commission-from__country-icon" />
            <span className="commission-from__landlord-name">{landlord?.name}</span>
          </If>

          <Form
            className={classNames('ant-advanced-search-form', {
              'commission-from__form--hidden': this.state.previewCommissionTier,
            })}
          >
            <TriggerPointForm
              t={t}
              triggerPoint={this.props.triggerPoint}
              value={this.state.triggerPoint}
              modalType={modalType}
              form={this.props.form}
              onChange={this.handleTriggerPointChange}
            />
            <Choose>
              <When condition={isCreateOrder}>
                <CreateForm
                  t={t}
                  form={this.props.form}
                  landlord={{ ...landlord }}
                  modalType={modalType}
                  currency={property.currency}
                  commissionLevel={commissionLevel}
                />
              </When>
              <Otherwise>
                <DetailsForm
                  t={t}
                  form={this.props.form}
                  currency={property.currency}
                  modalType={modalType}
                  showCommissionType={true}
                  showConvertCommission={true}
                  showBonus={true}
                />
                <ExtraRequirementForm t={t} form={this.props.form} />

                <If condition={commissionCategory !== allCommissionCategories.REBOOKERS_COMMISSION}>
                  <CommissionCapForm t={t} form={this.props.form} />
                </If>

                <If condition={commissionCategory !== allCommissionCategories.REBOOKERS_COMMISSION}>
                  <NoteForm t={t} form={this.props.form} />
                </If>

                <If condition={commissionLevel === commissionLevelConst.PROPERTY}>
                  <PropertiesForm
                    t={t}
                    form={this.props.form}
                    propertyId={property.id}
                    modalType={modalType}
                    landlordProperties={landlordProperties}
                    commissionRooms={edges}
                    commissionTier={currentCommissionTier}
                  />
                </If>
              </Otherwise>
            </Choose>
          </Form>

          <If condition={this.state.previewCommissionTier}>
            <div className="review-commission-modal-container">
              <div className="review-commission-modal-section">
                <ReviewCommission
                  action={this.state.previewCommissionAction}
                  commissionTier={this.state.previewCommissionTier}
                  properties={this.state.previewCommissionProperties}
                  displayType="section"
                />
              </div>
            </div>
          </If>
        </div>
        <div className="commission-from__footer">
          <div className="commission-from__footer__left">
            <If
              condition={
                modalType === 'edit' &&
                showElementByAuth(platformEntity.COMMISSION_COMISSION_TIERS, entityAction.CREATE) &&
                !this.state.previewCommissionTier
              }
            >
              <Choose>
                <When condition={this.props.form.isFieldsTouched()}>
                  <Tooltip
                    placement="top"
                    title={t('cms.property.commission_form_modal.copy.tips')}
                  >
                    <button
                      className="commission-from__duplicate-btn commission-from__duplicate-btn--disable"
                      type="button"
                    >
                      <CopyOutlined className="commission-from__copy-icon" />
                    </button>
                  </Tooltip>
                </When>
                <Otherwise>
                  <button
                    type="button"
                    className="commission-from__duplicate-btn"
                    onClick={this.handleDuplicate}
                    disabled={this.props.form.isFieldsTouched()}
                  >
                    <CopyOutlined className="commission-from__copy-icon" />
                  </button>
                </Otherwise>
              </Choose>
              <span className="commission-from__separator" />
              <Choose>
                <When condition={commissionLevel === commissionLevelConst.LANDLORD}>
                  <Popconfirm
                    placement="topLeft"
                    title={this.props.t(
                      'cms.properties.edit.commission.delete_commission_short_hint',
                    )}
                    onConfirm={e => {
                      e.stopPropagation();
                      this.handleDeleteCommission();
                    }}
                    onCancel={e => {
                      e.stopPropagation();
                    }}
                    okType="danger"
                    okText={this.props.t('cms.properties.edit.btn.yes')}
                    cancelText={this.props.t('cms.properties.edit.btn.no')}
                  >
                    <DeleteOutlined
                      className="commission-from__delete-commission-btn"
                      style={{ color: '#38b2a6' }}
                      onClick={e => {
                        e.stopPropagation();
                      }}
                    />
                  </Popconfirm>
                </When>
                <Otherwise>
                  <DeletePopconfirm
                    handleGotoPreview={() => {
                      this.handleGotoPreview(PreCalCommissionWhenPropertyTierChangeAction.DELETE);
                    }}
                    handleDeleteCommission={this.handleDeleteCommission}
                    t={this.props.t}
                  />
                </Otherwise>
              </Choose>
            </If>
          </div>

          <div className="commission-from__footer__right">
            <Button
              ghost
              size="large"
              className="commission-from__cancel-button"
              onClick={() => {
                if (this.state.previewCommissionTier) {
                  this.setState({
                    previewCommissionAction: null,
                    previewCommissionTier: null,
                    previewCommissionProperties: null,
                  });
                } else {
                  this.closeCommissionModal();
                }
              }}
            >
              <Choose>
                <When condition={this.state.previewCommissionTier}>
                  {t('cms.special_offer.back.btn')}
                </When>
                <Otherwise>{t('cms.referral.commission_plan.modal.btn.cancel')}</Otherwise>
              </Choose>
            </Button>
            <Choose>
              <When condition={commissionLevel === commissionLevelConst.LANDLORD && isCreateOrder}>
                <button
                  type="button"
                  onClick={this.handleSubmit}
                  className="commission-from__confirm-btn"
                >
                  {t('cms.listing.modal.confirm.btn')}
                </button>
              </When>
              <When
                condition={
                  commissionLevel === commissionLevelConst.PROPERTY &&
                  !this.state.previewCommissionTier &&
                  !isCreateOrder
                }
              >
                <Popconfirm
                  visible={this.state.showPreviewPopconfirm}
                  placement="topRight"
                  overlayClassName="commission-from__popover"
                  overlayStyle={{ maxWidth: 240 }}
                  title={
                    <div className="commission-from__popover__content">
                      <p>{t('cms.properties.edit.commission.update_commission_hint')}</p>
                      <p
                        className="commission-from__popover__link"
                        onClick={() => {
                          this.handleGotoPreview(
                            modalType === 'edit'
                              ? PreCalCommissionWhenPropertyTierChangeAction.UPDATE
                              : PreCalCommissionWhenPropertyTierChangeAction.CREATE,
                          );
                          this.setState({
                            showPreviewPopconfirm: false,
                          });
                        }}
                      >
                        {t('cms.properties.edit.commission.preview.popover.link')}
                      </p>
                    </div>
                  }
                  onConfirm={e => {
                    e.stopPropagation();
                    handleSubmit(e);
                    this.setState({
                      showPreviewPopconfirm: false,
                    });
                  }}
                  onCancel={e => {
                    e.stopPropagation();
                    this.setState({
                      showPreviewPopconfirm: false,
                    });
                  }}
                  okText={t('cms.properties.edit.btn.yes')}
                  okType="danger"
                  cancelText={t('cms.properties.edit.btn.no')}
                >
                  <button
                    onClick={() => {
                      const formField = this.getAllFields();
                      this.props.form.validateFieldsAndScroll(
                        formField,
                        { scroll: { offsetTop: 70, offsetBottom: 70 } },
                        err => {
                          if (!err) {
                            this.setState({
                              showPreviewPopconfirm: true,
                            });
                          }
                        },
                      );
                    }}
                    type="button"
                    className="commission-from__confirm-btn"
                  >
                    {t('cms.listing.modal.continue.btn')}
                  </button>
                </Popconfirm>
              </When>
              <Otherwise>
                <button
                  onClick={e => {
                    if (
                      this.state.previewCommissionTier &&
                      this.state.previewCommissionAction ===
                        PreCalCommissionWhenPropertyTierChangeAction.DELETE
                    ) {
                      this.handleDeleteCommission();
                    } else {
                      handleSubmit(e);
                    }
                  }}
                  type="button"
                  className="commission-from__confirm-btn"
                >
                  {t('cms.listing.modal.confirm.btn')}
                </button>
              </Otherwise>
            </Choose>
          </div>
        </div>
      </div>
    );
  }
}

CommissionFormModal.propTypes = {
  t: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  form: PropTypes.object,
  property: PropTypes.object,
  commissionCategory: PropTypes.string,
  modalType: PropTypes.string,
  correlationId: PropTypes.string,
  updateCommission: PropTypes.func,
  createCommission: PropTypes.func,
  commissionTiers: PropTypes.array,
  openModal: PropTypes.func,
  deleteCommission: PropTypes.func.isRequired,
  landlordProperties: PropTypes.array,
  bulkCreateCommissionTiers: PropTypes.func.isRequired,
};

CommissionFormModal.defaultProps = {
  t: () => {},
  form: {},
  property: {},
  onClose: () => {},
  commissionCategory: '',
  commissionRooms: {},
  modalType: '',
  correlationId: '',
  updateCommission: () => {},
  createCommission: () => {},
  commissionTiers: [],
  openModal: () => {},
  deleteCommission: () => {},
  landlordProperties: [],
  bulkCreateCommissionTiers: () => {},
};

export default Form.create({
  name: 'commission_form',
  onFieldsChange: (props, changedFields) => {
    if (changedFields && changedFields.category) {
      props.changeCommissionCategory(changedFields.category.value);
    }

    if (
      changedFields &&
      (changedFields.tenancyLengthTo || changedFields.tenancyLengthFrom || changedFields.capType)
    ) {
      if (
        !props.form.getFieldValue('tenancyLengthFrom') &&
        !props.form.getFieldValue('tenancyLengthTo') &&
        props.form.getFieldValue('capType') !== 'LENGTH'
      ) {
        props.form.resetFields(['tenancyUnit']);
      }
    }
  },
})(CommissionFormModal);
